import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { X, Search, UserPlus, UserCheck, Loader } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';
import { getDoc, doc } from 'firebase/firestore';
import { db } from '../config/firebase';

const FollowerModal = ({ isOpen, onClose, userId, type = 'followers', initialData = [] }) => {
  const { user: currentUser } = useAuth();
  const { followUser, unfollowUser, isFollowingUser } = useData();
  const [searchQuery, setSearchQuery] = useState('');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [followingStates, setFollowingStates] = useState({});
  const [processingFollows, setProcessingFollows] = useState({});

  // Fetch user details for each follower/following
  useEffect(() => {
    const fetchUserDetails = async () => {
      if (!isOpen || initialData.length === 0) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const userPromises = initialData.map(async (item) => {
          const targetUserId = type === 'followers' ? item.followerId : item.followingId;
          try {
            // Fetch user data
            const userDoc = await getDoc(doc(db, 'users', targetUserId));
            if (userDoc.exists()) {
              const userData = userDoc.data();
              
              // Fetch user profile
              const profileDoc = await getDoc(doc(db, 'userProfiles', targetUserId));
              const profileData = profileDoc.exists() ? profileDoc.data() : {};

              return {
                id: targetUserId,
                ...userData,
                profile: profileData,
                followedAt: item.followedAt
              };
            }
            return null;
          } catch (error) {
            console.error(`Error fetching user ${targetUserId}:`, error);
            return null;
          }
        });

        const fetchedUsers = await Promise.all(userPromises);
        const validUsers = fetchedUsers.filter(u => u !== null);
        setUsers(validUsers);

        // Check following states for all users
        if (currentUser) {
          const followingStatePromises = validUsers.map(async (user) => {
            const result = await isFollowingUser(currentUser.uid, user.id);
            return { userId: user.id, isFollowing: result };
          });

          const states = await Promise.all(followingStatePromises);
          const statesMap = {};
          states.forEach(({ userId, isFollowing }) => {
            statesMap[userId] = isFollowing;
          });
          setFollowingStates(statesMap);
        }
      } catch (error) {
        console.error('Error fetching user details:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserDetails();
  }, [isOpen, initialData, type, currentUser, isFollowingUser]);

  const handleFollow = async (targetUserId) => {
    if (!currentUser || processingFollows[targetUserId]) return;

    setProcessingFollows(prev => ({ ...prev, [targetUserId]: true }));
    try {
      const result = await followUser(currentUser.uid, targetUserId);
      if (result.success) {
        setFollowingStates(prev => ({ ...prev, [targetUserId]: true }));
      }
    } catch (error) {
      console.error('Error following user:', error);
    } finally {
      setProcessingFollows(prev => ({ ...prev, [targetUserId]: false }));
    }
  };

  const handleUnfollow = async (targetUserId) => {
    if (!currentUser || processingFollows[targetUserId]) return;

    setProcessingFollows(prev => ({ ...prev, [targetUserId]: true }));
    try {
      const result = await unfollowUser(currentUser.uid, targetUserId);
      if (result.success) {
        setFollowingStates(prev => ({ ...prev, [targetUserId]: false }));
      }
    } catch (error) {
      console.error('Error unfollowing user:', error);
    } finally {
      setProcessingFollows(prev => ({ ...prev, [targetUserId]: false }));
    }
  };

  // Filter users based on search query
  const filteredUsers = users.filter(user => {
    const searchLower = searchQuery.toLowerCase();
    const alias = user.profile?.alias || '';
    const name = user.displayName || '';
    const email = user.email || '';
    
    return (
      alias.toLowerCase().includes(searchLower) ||
      name.toLowerCase().includes(searchLower) ||
      email.toLowerCase().includes(searchLower)
    );
  });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">
            {type === 'followers' ? 'Followers' : 'Following'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X size={24} className="text-gray-600" />
          </button>
        </div>

        {/* Search Bar */}
        <div className="p-4 border-b border-gray-200">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search users..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* User List */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader className="animate-spin text-blue-600" size={32} />
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">
                {searchQuery ? 'No users found' : `No ${type} yet`}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredUsers.map((user) => (
                <div
                  key={user.id}
                  className="flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors"
                >
                  {/* Profile Photo */}
                  <Link to={`/profile/${user.id}`} onClick={onClose}>
                    {user.profile?.profilePhoto ? (
                      <img
                        src={user.profile.profilePhoto}
                        alt={user.profile.alias || user.displayName}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-[var(--pf-accent)] flex items-center justify-center text-white text-lg font-semibold">
                        {(user.profile?.alias || user.displayName || 'U').charAt(0).toUpperCase()}
                      </div>
                    )}
                  </Link>

                  {/* User Info */}
                  <div className="flex-1 min-w-0">
                    <Link 
                      to={`/profile/${user.id}`}
                      onClick={onClose}
                      className="block"
                    >
                      <h3 className="font-semibold text-gray-900 truncate hover:text-blue-600 transition-colors">
                        {user.profile?.alias || user.displayName || 'Anonymous'}
                      </h3>
                      {user.profile?.bio && (
                        <p className="text-sm text-gray-500 truncate">
                          {user.profile.bio}
                        </p>
                      )}
                      {user.role && (
                        <span className="inline-block mt-1 px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-700">
                          {user.role}
                        </span>
                      )}
                    </Link>
                  </div>

                  {/* Follow/Unfollow Button */}
                  {currentUser && currentUser.uid !== user.id && (
                    <button
                      onClick={() => followingStates[user.id] ? handleUnfollow(user.id) : handleFollow(user.id)}
                      disabled={processingFollows[user.id]}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                        followingStates[user.id]
                          ? 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                          : 'pf-btn-primary'
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      {processingFollows[user.id] ? (
                        <Loader size={16} className="animate-spin" />
                      ) : followingStates[user.id] ? (
                        <>
                          <UserCheck size={16} />
                          <span className="hidden sm:inline">Following</span>
                        </>
                      ) : (
                        <>
                          <UserPlus size={16} />
                          <span className="hidden sm:inline">Follow</span>
                        </>
                      )}
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 bg-gray-50">
          <p className="text-sm text-gray-600 text-center">
            {filteredUsers.length} {type === 'followers' ? 'follower' : 'following'}{filteredUsers.length !== 1 ? 's' : ''}
          </p>
        </div>
      </div>
    </div>
  );
};

export default FollowerModal;

